/**
 * CampaignUpdates — the public updates feed shown on a campaign detail page.
 *
 * Design intent: reads like a magazine feature's "letters from the road"
 * column. Student updates carry a small polaroid-caption feel; admin
 * updates get a subtle "From My Rich Aunt" badge so donors see when the
 * organization has confirmed enrollment, disbursed funds, or added
 * context.
 *
 * Donors and visitors can flag a student-authored update if they believe
 * it violates platform guidelines. Admin updates are trusted and cannot
 * be reported.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareText, Flag } from "lucide-react";
import { ShieldIcon } from "../lib/icons";
import {
  getUpdatesForCampaign,
  reportUpdate,
  subscribeUpdates,
} from "../lib/campaignUpdates";

const relTime = (iso) => {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.max(1, Math.round((now - then) / 1000));
  if (secs < 60)             return `${secs}s ago`;
  if (secs < 3600)           return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400)          return `${Math.round(secs / 3600)}h ago`;
  if (secs < 86400 * 30)     return `${Math.round(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const CampaignUpdates = ({ campaignId, studentFirstName }) => {
  const [updates, setUpdates] = useState(() => getUpdatesForCampaign(campaignId));

  useEffect(
    () => subscribeUpdates(() => setUpdates(getUpdatesForCampaign(campaignId))),
    [campaignId],
  );

  const onReport = (id) => {
    reportUpdate(id, "");
    toast.success("Thanks. We'll take a look.", {
      description: "The update stays visible while our team reviews it.",
    });
  };

  if (updates.length === 0) {
    return (
      <div
        data-testid="campaign-updates-empty"
        className="rounded-lg border border-ink/10 bg-sand p-6 text-[13px] text-olive"
      >
        No updates yet. When {studentFirstName || "the student"} posts one, you'll see it here.
      </div>
    );
  }

  return (
    <ul className="space-y-4" data-testid="campaign-updates-feed">
      {updates.map((u) => {
        const isAdmin = u.author === "admin";
        return (
          <li
            key={u.id}
            data-testid={`campaign-update-${u.id}`}
            className={`overflow-hidden rounded-lg border bg-sand ${
              isAdmin ? "border-cobalt/25" : "border-ink/10"
            }`}
          >
            {/* Header strip */}
            <div className="flex items-center justify-between gap-3 px-5 pt-4">
              <div className="flex items-center gap-2 text-[13px]">
                {isAdmin ? (
                  <>
                    <ShieldIcon className="h-4 w-4 text-cobalt" />
                    <span className="font-medium text-ink">{u.authorName}</span>
                    <span className="inline-flex items-center rounded-full bg-cobalt/10 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-[0.16em] text-cobalt">
                      From My Rich Aunt
                    </span>
                  </>
                ) : (
                  <>
                    <MessageSquareText className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
                    <span className="font-medium text-ink">{u.authorName}</span>
                    <span className="text-[13px] text-olive">· Student</span>
                  </>
                )}
              </div>
              <span className="text-[13px] text-olive">{relTime(u.postedAt)}</span>
            </div>

            {/* Body */}
            <p className="mt-3 px-5 pb-4 text-base leading-relaxed text-ink">
              {u.body}
            </p>

            {/* Report affordance (student updates only) */}
            {!isAdmin && (
              <div className="flex items-center justify-between border-t border-ink/10 bg-beige/60 px-5 py-2">
                <span className="text-[13px] uppercase tracking-[0.22em] text-olive">
                  Community update
                </span>
                {u.state === "reported" ? (
                  <span
                    data-testid={`update-reported-${u.id}`}
                    className="inline-flex items-center gap-1 text-[13px] font-medium text-olive"
                  >
                    <Flag className="h-3 w-3" strokeWidth={2} /> Reported. Under review.
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onReport(u.id)}
                    data-testid={`update-report-${u.id}`}
                    className="inline-flex items-center gap-1 text-[13px] text-olive hover:text-scarlet"
                  >
                    <Flag className="h-3 w-3" strokeWidth={2} /> Report
                  </button>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};
